import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetExpenseCategoryByIdQuery } from "@generated";
import { ExpenseCategoryForm } from "../ExpenseCategoryForm/ExpenseCategoryForm";
import { useExpenseCategoryMutations } from "../api";
import { emptyExpenseCategoryForm } from "../types";
import type {
  ExpenseCategoryFormValues,
  ExpenseCategoryStatus,
} from "../types";

const FORM_ID = "expense-category-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useExpenseCategoryMutations();
  const { data, isLoading, refetch } = useV2GetExpenseCategoryByIdQuery(
    {
      id: Number(id),
      include: ["phase"],
    },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<ExpenseCategoryFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<ExpenseCategoryFormValues>(() => {
    const record: any = data?.data;
    const attrs = record?.attributes;
    if (!attrs) return emptyExpenseCategoryForm;
    return {
      name: attrs.name ?? "",
      phaseId: record.relationships?.phase?.data?.id
        ? String(record.relationships.phase.data.id)
        : null,
      status: (attrs.status ?? "optional") as ExpenseCategoryStatus,
      rate: attrs.rate ?? null,
      markup: attrs.markup ?? null,
      useResourceRate: !!attrs["use-resource-rate"],
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: ExpenseCategoryFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update expense category", err);
      setToast({
        variant: "danger",
        text: "Could not save expense category.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Expense Category"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <ExpenseCategoryForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
